import React, { useCallback, useMemo } from 'react';
import ValidInputError from './ValidInputError';
import { ValidOptionalInput, ValidOptionalProp } from './types';
import ValidErrorMarker from './ValidErrorMarker';
import ValidInputLabel from './ValidInputLabel';
import apiUtil from '../../utils/apiUtil';
import { handleTextInputValue } from './handle-text-input-value';

export type InputTextType = 'None' | 'Integer' | 'Ndc' | 'Phone/Fax' | 'Zip';

// TYPE
export type ValidPropText = {
    key: string;
    lbl?: string;
    val: string;
    err: string;
};

// VIEW MODEL
export function validPropTextVM(key: string, lbl?: string, val?: string, err?: string): ValidPropText {
    return {
        key: key,
        lbl: !!lbl ? lbl : '',
        val: !!val ? val : '',
        err: !!err ? err : '',
    };
}

export function validOptionalPropTextVM(
    key: string,
    lbl?: string,
    val?: string,
    err?: string,
    checked?: boolean,
): ValidOptionalProp<any> {
    return {
        key: key,
        lbl: !!lbl ? lbl : '',
        val: !!val ? val : '',
        err: !!err ? err : '',
        checked: checked ?? false,
    };
}

// INPUT COMPONENT
export type ValidInputTextParams = ValidOptionalInput & {
    prop: ValidPropText;
    width?: number;
    maxlen?: number;
    disabled?: boolean;
    placeholder?: string;
    type?: InputTextType;
    onChange: (prop: string, val: string) => void;
    alternateLabel?: string;
    fullWidth?: boolean;
    hideError?: boolean;
    labelNoMinWidth?: boolean;
};
const ValidInputText = ({
    prop,
    width,
    maxlen,
    disabled,
    placeholder,
    onChange,
    type,
    alternateLabel,
    checked,
    onCheckboxChange,
    fullWidth,
    hideError = false,
    labelNoMinWidth = false,
}: ValidInputTextParams): JSX.Element => {
    const id = 'vpText' + prop.key;
    const inputWidth = !!width ? width : '';
    const promptTxt = !!placeholder ? placeholder : '';
    const showCheckbox = !!onCheckboxChange && checked !== undefined;
    const value = useMemo(() => {
        let result = prop.val;
        if (type === 'Phone/Fax') {
            result = apiUtil.toPhoneNumber(prop.val);
        } else if (type === 'Zip') {
            result = apiUtil.toZipCode(prop.val);
        }
        return result;
    }, [prop.val, type]);

    const handleChange = useCallback(
        (e): void => {
            const val = e.target.value;
            const value = handleTextInputValue(type, val);
            onChange(prop.key, value);
        },
        [onChange, prop.key, type],
    );

    const handleCheckboxChange = useCallback((): void => {
        if (showCheckbox) {
            onCheckboxChange(!checked);
        }
    }, [checked, onCheckboxChange, showCheckbox]);

    return (
        <div
            id={id}
            className="component-valid-input component-valid-input-text"
        >
            <div className={`input-wrapper ${fullWidth ? 'full-width' : ''}`}>
                {prop.lbl ? (
                    <ValidInputLabel
                        label={alternateLabel ?? prop.lbl}
                        onClick={showCheckbox ? handleCheckboxChange : undefined}
                        showCheckbox={showCheckbox}
                        checked={checked}
                        noMinWidth={labelNoMinWidth}
                    />
                ) : null}
                <input
                    name={prop.key}
                    className="valid-input input-text"
                    type="text"
                    disabled={disabled}
                    placeholder={promptTxt}
                    maxLength={maxlen}
                    style={{ width: inputWidth }}
                    value={value}
                    onChange={handleChange}
                />
                {hideError ? null : <ValidErrorMarker showMarker={!!prop.err} />}
            </div>
            {hideError ? null : (
                <ValidInputError
                    err={prop.err}
                    showMsg
                />
            )}
        </div>
    );
};

export default ValidInputText;
